
import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";

function NewsCarousel({ featuredSection = [] }) {
  const [current, setCurrent] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const carouselRef = useRef(null);

  // Get featured news items from the data
  const newsItems = featuredSection || [];

  // Auto-advance the carousel
  useEffect(
    () => {
      if (newsItems.length <= 1) return;

      const interval = setInterval(() => {
        setCurrent(prevCurrent => (prevCurrent + 1) % newsItems.length);
      }, 5000);

      return () => clearInterval(interval);
    },
    [newsItems.length]
  );

  const handlePrevious = () => {
    setCurrent(
      prevCurrent => (prevCurrent - 1 + newsItems.length) % newsItems.length
    );
  };

  const handleNext = () => {
    setCurrent(prevCurrent => (prevCurrent + 1) % newsItems.length);
  };

  // Add touch/mouse drag functionality
  const handleMouseDown = e => {
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleTouchStart = e => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.clientX;
    const walk = startX - x;

    if (Math.abs(walk) > 50) {
      if (walk > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
      setIsDragging(false);
    }
  };

  const handleTouchMove = e => {
    if (!isDragging) return;
    const x = e.touches[0].clientX;
    const walk = startX - x;

    if (Math.abs(walk) > 50) {
      if (walk > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
      setIsDragging(false);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="w-full max-w-3xl mx-auto relative">
      <div
        ref={carouselRef}
        className={`w-full overflow-hidden ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleDragEnd}
      >
        <div
          className="flex h-[400px] transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {newsItems.map((item, index) => (
            <div onClick={()=>window.open(item.sourceurl,"_blank")} key={item.id || index} className="w-full flex-shrink-0">
              <Card className="border-0 overflow-hidden h-full">
                <CardContent className="p-0 h-full">
                  {/* Fixed image container */}
                  <div className="relative w-full h-full rounded-xl overflow-hidden">
                    <img
                      src={item.imageurl || "/placeholder.svg"}
                      alt={item.subject || "News item"}
                      className="w-full h-full object-cover rounded-xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent rounded-xl" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h2 className="text-xl md:text-2xl font-bold">
                        {item.subject || "News headline"}
                      </h2>
                      <p className="text-yellow-400 text-sm mt-1 mb-8">
                        Source: {item.source || "Unknown"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Dash-style pagination indicator with gaps */}
      <div className="absolute bottom-10 left-0 right-0 flex justify-center z-10">
        <div className="flex gap-2 items-center">
          {newsItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className={`h-1 w-12 rounded-sm transition-all duration-300 ease-out ${
                index === current
                  ? "bg-yellow-400"
                  : "bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {newsItems.length > 1 && (
        <>
          <button
            className="absolute left-5 top-1/2 -translate-y-1/2 bg-black/30 text-white border-none hover:bg-black/50 w-10 h-10 rounded-full flex items-center justify-center"
            onClick={handlePrevious}
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            className="absolute right-5 top-1/2 -translate-y-1/2 bg-black/30 text-white border-none hover:bg-black/50 w-10 h-10 rounded-full flex items-center justify-center"
            onClick={handleNext}
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}
    </div>
  );
}

export default NewsCarousel;
